import { useState, type FormEvent } from 'react';
import { Search, Gift, Plus, Copy, Trash2 } from 'lucide-react';
import type { RedeemCode, Redemption } from './types';
import toast from '../Toast';
import { formatBeijingDate, formatBeijingDateTime } from '../../utils/time';

interface CreateForm {
  credits: number;
  count: number;
  maxUses: number;
  expiresAt: string;
  prefix: string;
}

interface RedeemCodesTabProps {
  redeemCodes: RedeemCode[];
  redemptions: Redemption[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onCreateCodes: (e: FormEvent) => void;
  onDisableCode: (codeId: string) => void;
  createForm: CreateForm;
  onCreateFormChange: (form: CreateForm) => void;
  createdCodes: RedeemCode[];
  onClearCreatedCodes: () => void;
}

export default function RedeemCodesTab({
  redeemCodes,
  redemptions,
  searchQuery,
  onSearchChange,
  onCreateCodes,
  onDisableCode,
  createForm,
  onCreateFormChange,
  createdCodes,
  onClearCreatedCodes,
}: RedeemCodesTabProps) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showRedemptions, setShowRedemptions] = useState(false);

  const filteredCodes = redeemCodes.filter(c =>
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('已复制到剪贴板');
  };

  return (
    <div className="space-y-6">
      {/* Create Form */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border p-4 sm:p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-blue rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-light-text">创建兑换码</h3>
              <p className="text-sm text-light-muted">批量生成积分兑换码</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn-secondary w-full text-sm sm:w-auto"
          >
            {showCreateForm ? '收起' : '展开'}
          </button>
        </div>

        {showCreateForm && (
          <form onSubmit={onCreateCodes} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">积分数量</label>
                <input
                  type="number"
                  min="1"
                  value={createForm.credits}
                  onChange={(e) => onCreateFormChange({ ...createForm, credits: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">生成数量</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={createForm.count}
                  onChange={(e) => onCreateFormChange({ ...createForm, count: parseInt(e.target.value) || 1 })}
                  className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">使用次数限制</label>
                <input
                  type="number"
                  min="1"
                  value={createForm.maxUses}
                  onChange={(e) => onCreateFormChange({ ...createForm, maxUses: parseInt(e.target.value) || 1 })}
                  className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                />
                <p className="text-xs text-light-muted mt-1">每个用户可使用的次数</p>
              </div>
              <div>
                <label className="text-sm font-medium text-light-text mb-1.5 block">过期时间（可选）</label>
                <input
                  type="datetime-local"
                  value={createForm.expiresAt}
                  onChange={(e) => onCreateFormChange({ ...createForm, expiresAt: e.target.value })}
                  className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-light-text mb-1.5 block">前缀（可选）</label>
              <input
                type="text"
                value={createForm.prefix}
                onChange={(e) => onCreateFormChange({ ...createForm, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder="如: VIP"
                className="w-full bg-white border border-light-border rounded-xl px-4 py-3 text-light-text placeholder-light-muted focus:outline-none focus:border-accent-blue focus:ring-2 focus:ring-accent-blue/20 shadow-sm transition-colors uppercase"
              />
              <p className="text-xs text-light-muted mt-1">兑换码将显示为: 前缀-随机码</p>
            </div>
            <button
              type="submit"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-blue px-6 py-3 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              创建兑换码
            </button>
          </form>
        )}

        {/* Created Codes */}
        {createdCodes.length > 0 && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <h4 className="text-sm font-semibold text-green-800 mb-3">创建成功！请保存以下兑换码：</h4>
            <div className="space-y-2">
              {createdCodes.map((code) => (
                <div key={code.id} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <code className="flex-1 break-all bg-white border border-green-200 rounded-lg px-3 py-2 text-sm font-mono text-green-800">
                    {code.code}
                  </code>
                  <button
                    onClick={() => handleCopyCode(code.code)}
                    className="flex items-center justify-center rounded-lg p-2 text-green-600 transition-colors hover:bg-green-100 sm:self-auto"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={onClearCreatedCodes}
              className="mt-3 text-sm text-green-700 hover:underline"
            >
              清除
            </button>
          </div>
        )}
      </div>

      {/* Codes List */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="p-4 border-b border-light-border flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-muted" />
            <input
              type="text"
              placeholder="搜索兑换码..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-light-border rounded-xl focus:outline-none focus:border-accent-blue"
            />
          </div>
          <button
            onClick={() => setShowRedemptions(!showRedemptions)}
            className="btn-secondary w-full whitespace-nowrap text-sm sm:w-auto"
          >
            {showRedemptions ? '显示兑换码' : '显示兑换记录'}
          </button>
        </div>

        {!showRedemptions ? (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-light-hover">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">兑换码</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">积分</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">状态</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">使用次数</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">过期时间</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map((code) => (
                    <tr key={code.id} className="border-t border-light-border">
                      <td className="px-4 py-3">
                        <code className="text-sm font-mono bg-light-hover px-2 py-1 rounded">{code.code}</code>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-light-text">{code.credits}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-lg text-xs ${
                          code.status === 'active'
                            ? 'bg-green-100 text-green-600'
                            : code.status === 'used'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {code.status === 'active' ? '有效' : code.status === 'used' ? '已用完' : '已禁用'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-light-text">
                          {code.usedCount} / {code.maxUses || '∞'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-light-muted">
                          {code.expiresAt ? formatBeijingDateTime(code.expiresAt) : '永不过期'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCopyCode(code.code)}
                            className="p-2 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors"
                            title="复制"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {code.status === 'active' && (
                            <button
                              onClick={() => onDisableCode(code.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="禁用"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-light-border">
              {filteredCodes.map((code) => (
                <div key={code.id} className="p-4">
                  {/* Header */}
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <code className="min-w-0 break-all text-sm font-mono bg-light-hover px-2 py-1 rounded">{code.code}</code>
                    <span className={`px-2 py-1 rounded-lg text-xs ${
                      code.status === 'active'
                        ? 'bg-green-100 text-green-600'
                        : code.status === 'used'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-red-100 text-red-600'
                    }`}>
                      {code.status === 'active' ? '有效' : code.status === 'used' ? '已用完' : '已禁用'}
                    </span>
                  </div>

                  {/* Info Grid */}
                  <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <div className="bg-light-hover rounded-lg p-2 text-center">
                      <p className="text-xs text-light-muted">积分</p>
                      <p className="text-sm font-medium text-light-text">{code.credits}</p>
                    </div>
                    <div className="bg-light-hover rounded-lg p-2 text-center">
                      <p className="text-xs text-light-muted">使用</p>
                      <p className="text-sm font-medium text-light-text">
                        {code.usedCount} / {code.maxUses || '∞'}
                      </p>
                    </div>
                    <div className="bg-light-hover rounded-lg p-2 text-center">
                      <p className="text-xs text-light-muted">过期</p>
                      <p className="text-xs text-light-text">
                        {code.expiresAt ? formatBeijingDate(code.expiresAt) : '永不过期'}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      onClick={() => handleCopyCode(code.code)}
                      className="flex items-center justify-center gap-1 px-3 py-2 text-sm text-accent-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      复制
                    </button>
                    {code.status === 'active' && (
                      <button
                        onClick={() => onDisableCode(code.id)}
                        className="flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        禁用
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-light-hover">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">兑换码</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">用户</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">积分</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-light-text">兑换时间</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r) => (
                    <tr key={r.id} className="border-t border-light-border">
                      <td className="px-4 py-3">
                        <code className="text-sm font-mono bg-light-hover px-2 py-1 rounded">{r.code}</code>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-light-text">{r.userEmail}</p>
                          <p className="text-xs text-light-muted">{r.userName || '未设置昵称'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-light-text">{r.credits}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-light-muted">
                          {formatBeijingDateTime(r.createdAt)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-light-border">
              {redemptions.map((r) => (
                <div key={r.id} className="p-4">
                  {/* Header */}
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <code className="min-w-0 break-all text-sm font-mono bg-light-hover px-2 py-1 rounded">{r.code}</code>
                    <span className="text-sm font-medium text-accent-blue">{r.credits} 积分</span>
                  </div>

                  {/* User Info */}
                  <div className="mb-2">
                    <p className="text-sm text-light-text">{r.userEmail}</p>
                    <p className="text-xs text-light-muted">{r.userName || '未设置昵称'}</p>
                  </div>

                  {/* Time */}
                  <p className="text-xs text-light-muted">
                    {formatBeijingDateTime(r.createdAt)}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
